"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Image from "next/image";

import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import infoIcon from "@repo/ui/assets/payment/info.svg";
import userIcon from "@repo/ui/assets/payment/user-card.svg";
import { InputField } from "@repo/ui/components/common/forms/InputField";
import { RoundedCheckbox } from "@repo/ui/components/TermsAndContionsCard";
import { Card, CardContent } from "@repo/ui/components/ui/card";
import { Form, FormField } from "@repo/ui/components/ui/form";
import { COUNTRIES, COUNTRIES_CALLING_CODES } from "@repo/ui/data/countries";
import { cn } from "@repo/ui/lib/utils";
import type { PaymentSection } from "@repo/ui/types/cms/payment";

import { useAppDispatch, useAppSelector } from "@repo/store/hooks";
import type { ContactInfo } from "@repo/store/slices";
import {
  setContactErrorMessage,
  setContactInfo,
  updateContactInfo,
  useAuthState,
  useBookingDetails,
  useContactErrorMessage,
  useIsPaymentRetry,
  useIsUserStaffOrMember,
  useLocale,
  useManageBooking,
  useOriginalBookingDetails,
  usePassenger,
  usePaymentPNR,
} from "@repo/store/slices";

import { phoneCountryCode } from "../../constants/countries";
import { parseContactPhone } from "../../helpers/contactPhone";

// Zod Schema
const contactInfoSchema = z
  .object({
    isFirstPassenger: z.boolean(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().min(1, "Email is required").email("Enter a valid email"),
    country: z.string().min(1, "Country is required"),
    phoneNumber: z
      .string()
      .min(1, "Phone number is required") // Custom validation usually, but we want required. Original used validation type for length.
      .refine((val) => val.trim().length > 0, { message: "Phone number is required" }), // required
    phoneNumberPrefix: z.string().optional(),
    RUC: z.string().optional(),

    // Confirmation fields
    confirmEmail: z.string().optional(),
    confirmCountry: z.string().optional(),
    confirmPhone: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.isFirstPassenger) {
      if (data.firstName.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "First Name is required",
          path: ["firstName"],
        });
      } else if (data.firstName.length > 50) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "First Name cannot exceed 50 characters",
          path: ["firstName"],
        });
      } else if (!/^[\p{L}\s\-']+$/u.test(data.firstName)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "First Name can only contain letters",
          path: ["firstName"],
        });
      }

      if (data.lastName.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Last Name is required",
          path: ["lastName"],
        });
      } else if (data.lastName.length > 50) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Last Name cannot exceed 50 characters",
          path: ["lastName"],
        });
      } else if (!/^[\p{L}\s\-']+$/u.test(data.lastName)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Last Name can only contain letters",
          path: ["lastName"],
        });
      }
    }

    if (data.confirmEmail && data.confirmEmail.trim() !== "" && data.confirmEmail !== data.email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Email addresses do not match",
        path: ["confirmEmail"],
      });
    }

    if (
      data.confirmCountry &&
      data.confirmCountry.trim() !== "" &&
      data.confirmCountry !== data.country
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Country codes do not match",
        path: ["confirmCountry"],
      });
    }

    if (
      data.confirmPhone &&
      data.confirmPhone.trim() !== "" &&
      data.confirmPhone !== data.phoneNumber
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Phone numbers do not match",
        path: ["confirmPhone"],
      });
    }

    if (data.country && data.phoneNumber) {
      const phoneNumberLength = data.phoneNumber.replaceAll(/\D/g, "").length;
      if (
        (data.country === phoneCountryCode.Chile && phoneNumberLength !== 9) ||
        phoneNumberLength < 9
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "The field must have 9 characters length.",
          path: ["phoneNumber"],
        });
      }

      if (data.country !== phoneCountryCode.Chile && phoneNumberLength > 16) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "The maximum length is 16 characters.",
          path: ["phoneNumber"],
        });
      }
    }
  });

type ContactInfoFormData = z.infer<typeof contactInfoSchema>;

type ContactInfoCardProps = Readonly<{
  pageData: PaymentSection;
}>;

declare global {
  interface GlobalThis {
    __validatePassengersForm: () => boolean;
    __validateContactInfo?: () => boolean;
    __submitPayment?: () => Promise<void>;
    __passengers_checking_in?: Record<string, boolean>;
    __payment_passenger_email?: string;
    __validateBillingInfo?: () => Promise<boolean>;
  }

  var __validatePassengersForm: (() => boolean) | undefined;
  var __validateContactInfo: (() => boolean) | undefined;
  var __submitPayment: (() => Promise<void>) | undefined;
  var __passengers_checking_in: Record<string, boolean> | undefined;
  var __payment_passenger_email: string | undefined;
  var __validateBillingInfo: (() => Promise<boolean>) | undefined;
}

export default function ContactInfoCard({ pageData }: ContactInfoCardProps) {
  const dispatch = useAppDispatch();
  const bookingDetails = useBookingDetails();
  const originalBookingDetails = useOriginalBookingDetails();
  const firstPassenger = usePassenger(0);
  const paymentPNR = usePaymentPNR();
  const isPaymentRetry = useIsPaymentRetry();
  const contactErrorMessage = useContactErrorMessage();
  const isUserStaffOrMember = useIsUserStaffOrMember();
  const authState = useAuthState();
  const locale = useLocale();
  const [maxLengthReached, setMaxLengthReached] = useState(false);
  const bookingSummary = useManageBooking();

  // Names on an existing booking are read-only, except when retrying a declined payment
  const isContactLocked = Boolean(
    paymentPNR && !isPaymentRetry && Number(bookingSummary?.contacts?.length) > 0,
  );

  const contactInfo = useAppSelector((state) => {
    const currentPNR = state.payment.pnr;
    let contactInfo = state.payment.contactInfo;
    const bookingSummary = state.manageBooking.raw;

    if (currentPNR) {
      const bookingContactInfo =
        bookingSummary?.contacts?.find((c) => c.contactTypeCode === "P" && c.phone?.trim()) ??
        bookingSummary?.contacts?.find((c) => c.phone?.trim()) ??
        bookingSummary?.contacts?.[0];

      if (Number(bookingSummary?.contacts?.length) === 0) return contactInfo;

      const { phoneNumber, phoneNumberPrefix } = parseContactPhone(
        bookingContactInfo?.phone?.trim() ?? "",
      );

      contactInfo = {
        isFirstPassenger: false,
        firstName: contactInfo.firstName || bookingContactInfo!.firstname,
        lastName: contactInfo.lastName || bookingContactInfo!.lastname,
        email: contactInfo.email || bookingContactInfo!.email,
        country:
          contactInfo.country ||
          (phoneNumberPrefix
            ? phoneNumberPrefix === "1"
              ? "US-1"
              : (COUNTRIES_CALLING_CODES?.find((c) => c.prefix === phoneNumberPrefix)?.value ??
                contactInfo.country)
            : contactInfo.country),
        phoneNumber: contactInfo.phoneNumber || phoneNumber || "",
        phoneNumberPrefix:
          contactInfo.phoneNumberPrefix ||
          phoneNumberPrefix ||
          (COUNTRIES_CALLING_CODES?.[0]?.prefix ?? ""),
      };
    }

    return contactInfo;
  });

  const defaultCountry = COUNTRIES_CALLING_CODES?.[0]?.value ?? "";

  const localeCountry = useMemo(() => {
    const localeIsoCode = locale.split(/[-_]/)[1]?.toUpperCase();
    const country = COUNTRIES.find((c) => c.isoCode === localeIsoCode);
    return country ? `${country.isoCode}-${country.callingCode}` : defaultCountry;
  }, [locale, defaultCountry]);

  const formDefaultValues = useMemo(
    () => ({
      isFirstPassenger: false,
      firstName: contactInfo?.firstName ?? "",
      lastName: contactInfo?.lastName ?? "",
      email: contactInfo?.email ?? "",
      country: contactInfo?.country || (paymentPNR ? localeCountry : defaultCountry),
      phoneNumber: contactInfo?.phoneNumber ?? "",
      phoneNumberPrefix:
        contactInfo?.phoneNumberPrefix ||
        (paymentPNR
          ? (localeCountry.split("-")[1] ?? "")
          : (COUNTRIES_CALLING_CODES?.[0]?.prefix ?? "")),
      RUC: "",
      // TEMP: mirror contact info into confirm fields for prefilled testing — remove before shipping.
      confirmEmail: contactInfo?.email ?? "",
      confirmCountry: contactInfo?.country || (paymentPNR ? localeCountry : ""),
      confirmPhone: contactInfo?.phoneNumber ?? "",
    }),
    [
      contactInfo?.country,
      contactInfo?.email,
      contactInfo?.firstName,
      contactInfo?.lastName,
      contactInfo?.phoneNumber,
      contactInfo?.phoneNumberPrefix,
      defaultCountry,
      localeCountry,
      paymentPNR,
    ],
  );

  const form = useForm<ContactInfoFormData>({
    resolver: zodResolver(contactInfoSchema),
    defaultValues: formDefaultValues,
    mode: "onChange",
  });

  const {
    control,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors, isValid },
  } = form;

  const hasInitializedFromBooking = useRef(false);
  const initialEmailRef = useRef<string>("");
  const initialPhoneRef = useRef<string>("");
  const initialCountryRef = useRef<string>("");

  useEffect(() => {
    if (
      !paymentPNR ||
      Number(bookingSummary?.contacts?.length) === 0 ||
      hasInitializedFromBooking.current
    ) {
      return;
    }

    const initEmail = contactInfo?.email ?? "";
    const initPhone = contactInfo?.phoneNumber ?? "";
    const initCountry = contactInfo?.country ?? "";

    initialEmailRef.current = initEmail;
    initialPhoneRef.current = initPhone;
    initialCountryRef.current = initCountry;

    if (initEmail) {
      setValue("email", initEmail, { shouldValidate: true });
      setValue("confirmEmail", initEmail, { shouldValidate: true });
    }
    if (initPhone) {
      setValue("phoneNumber", initPhone, { shouldValidate: true });
      setValue("confirmPhone", initPhone, { shouldValidate: true });
    }
    if (initCountry) {
      setValue("country", initCountry, { shouldValidate: true });
      setValue("confirmCountry", initCountry, { shouldValidate: true });
    }
    hasInitializedFromBooking.current = true;
  }, [
    bookingSummary?.contacts?.length,
    contactInfo?.country,
    contactInfo?.email,
    contactInfo?.phoneNumber,
    paymentPNR,
    setValue,
  ]);

  const isFirstPassengerChecked = watch("isFirstPassenger");
  const email = watch("email");
  const phoneNumber = watch("phoneNumber");
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const countryCode = watch("country");

  const isEmailModified = Boolean(
    paymentPNR && initialEmailRef.current && email !== initialEmailRef.current,
  );
  const isPhoneModified = Boolean(
    paymentPNR && initialPhoneRef.current && phoneNumber !== initialPhoneRef.current,
  );
  const isContactModified = isEmailModified || isPhoneModified;

  const phoneNumberPrefix = useMemo(() => {
    return (
      COUNTRIES_CALLING_CODES?.find((c) => c.value === countryCode)?.prefix ??
      COUNTRIES_CALLING_CODES?.[0]?.prefix ??
      ""
    );
  }, [countryCode]);

  useEffect(() => {
    if (phoneNumberPrefix) {
      dispatch(updateContactInfo({ phoneNumberPrefix }));
    }
  }, [phoneNumberPrefix, dispatch]);

  useEffect(() => {
    if (contactInfo?.country || paymentPNR) return;

    const localeIsoCode = locale.split(/[-_]/)[1]?.toUpperCase();
    const country = COUNTRIES.find((c) => c.isoCode === localeIsoCode);

    if (country) {
      const value = `${country.isoCode}-${country.callingCode}`;
      dispatch(updateContactInfo({ country: value, phoneNumberPrefix: country.callingCode }));
      setValue("country", value);
    }
  }, [dispatch, locale, setValue, paymentPNR, contactInfo?.country]);

  // State for confirmation fields
  const [showConfirmFields, setShowConfirmFields] = useState(false);
  const confirmEmail = watch("confirmEmail");
  const confirmPhone = watch("confirmPhone");
  const confirmCountry = watch("confirmCountry");

  // Additional email state
  const [showAdditionalEmail, setShowAdditionalEmail] = useState(!!contactInfo?.additionalEmail);
  const [additionalEmail, setAdditionalEmail] = useState(contactInfo?.additionalEmail ?? "");

  const addAdditionalEmail = () => {
    setShowAdditionalEmail(true);
  };

  const removeAdditionalEmail = () => {
    setShowAdditionalEmail(false);
    setAdditionalEmail("");
    handleFormChange("additionalEmail", "");
  };

  // Validate additional email
  const isValidEmail = useCallback(
    (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim()),
    [],
  );
  const additionalEmailError =
    additionalEmail && !isValidEmail(additionalEmail) ? "Enter a valid email" : "";

  // Sync with First Passenger
  const populateWithFirstPassenger = useCallback(
    (isFirstPassengerChecked: boolean = true) => {
      // if (firstPassenger) {
      const { email, country, phoneNumber } = getValues();

      const contact = (bookingDetails?.contacts["P"] ||
        originalBookingDetails?.contacts?.["P"]) as {
        name: {
          first: string;
          last: string;
        };
        emailAddress: string;
        phoneNumbers: {
          type: string;
          number: string;
        }[];
      };

      const rawPhone =
        contact?.phoneNumbers && contact.phoneNumbers.length > 0 && contact.phoneNumbers[0]?.number
          ? contact.phoneNumbers[0].number
          : phoneNumber || "";
      const parsedPhone = parseContactPhone(rawPhone);
      const nationalNumber = parsedPhone.phoneNumber;

      const updatedContactInfo: ContactInfo = {
        isFirstPassenger: true,
        firstName: firstPassenger?.firstName ?? (contact?.name.first || ""),
        lastName: firstPassenger?.lastName ?? (contact?.name.last || ""),
        email: isUserStaffOrMember ? (authState.user?.email ?? "") : contact?.emailAddress || email,
        country,
        phoneNumber: nationalNumber,
        phoneNumberPrefix: parsedPhone.phoneNumberPrefix || phoneNumberPrefix,
      };

      setValue("firstName", firstPassenger?.firstName ?? (contact?.name.first || ""), {
        shouldValidate: true,
      });
      setValue("lastName", firstPassenger?.lastName ?? (contact?.name.last || ""), {
        shouldValidate: true,
      });
      if (isFirstPassengerChecked) {
        setValue("isFirstPassenger", true);
      }
      setValue("country", country, { shouldValidate: true });
      setValue("confirmCountry", country, { shouldValidate: true });

      setValue("email", authState.user?.email ?? (contact?.emailAddress || ""), {
        shouldValidate: true,
      });
      setValue("phoneNumber", nationalNumber, { shouldValidate: true });
      dispatch(setContactInfo(updatedContactInfo));
      // }
    },
    [
      authState.user?.email,
      dispatch,
      firstPassenger,
      getValues,
      isUserStaffOrMember,
      phoneNumberPrefix,
      setValue,
      bookingDetails?.contacts,
      originalBookingDetails?.contacts,
    ],
  );

  const clearFirstPassengerData = useCallback(() => {
    const { country, phoneNumber } = getValues();
    const updatedContactInfo: ContactInfo = {
      isFirstPassenger: false,
      firstName: "",
      lastName: "",
      email: "",
      country,
      phoneNumber,
      phoneNumberPrefix,
    };

    setValue("firstName", "");
    setValue("lastName", "");
    setValue("isFirstPassenger", false);
    setValue("email", "");
    setValue("phoneNumber", "");

    // Trigger validation to show required errors
    trigger(["firstName", "lastName"]);

    dispatch(setContactInfo(updatedContactInfo));
  }, [dispatch, getValues, phoneNumberPrefix, setValue, trigger]);

  useEffect(() => {
    if (!paymentPNR) {
      if (isFirstPassengerChecked) {
        populateWithFirstPassenger();
      } else if (!isFirstPassengerChecked) {
        if (globalThis.location.pathname.includes("/modify-booking/payment")) {
          populateWithFirstPassenger(isFirstPassengerChecked);
        }
        // no-op, relying on user action to specific uncheck in UI handler to avoid clearing on mount
      }
    }
  }, [isFirstPassengerChecked, firstPassenger, populateWithFirstPassenger, paymentPNR]);

  // Sync to Redux on change
  const handleFormChange = <K extends keyof ContactInfo>(field: K, value: ContactInfo[K]) => {
    dispatch(updateContactInfo({ [field]: value }));
  };

  const toDigits = useCallback((val: string) => val.replaceAll(/\D/g, ""), []);

  // Expose validation for Pay Now button
  useEffect(() => {
    // Synchronous validation to satisfy TS requirement (boolean return)
    globalThis.__validateContactInfo = () => {
      // Use isValid from formState (updated in onChange mode)
      const isFormValid = isValid;

      let areConfirmFieldsValid = true;

      // Additional checks for validation of confirmation visibility and required-ness
      if (showConfirmFields) {
        const values = getValues();
        let hasEmptyConfirm = false;
        if ((!paymentPNR || isEmailModified) && !values.confirmEmail?.trim()) {
          form.setError("confirmEmail", { type: "required", message: "" });
          hasEmptyConfirm = true;
        }
        if ((!paymentPNR || isPhoneModified) && !values.confirmCountry?.trim()) {
          form.setError("confirmCountry", { type: "required", message: "" });
          hasEmptyConfirm = true;
        }
        if ((!paymentPNR || isPhoneModified) && !values.confirmPhone?.trim()) {
          form.setError("confirmPhone", { type: "required", message: "" });
          hasEmptyConfirm = true;
        }

        if (hasEmptyConfirm) {
          areConfirmFieldsValid = false;
        } else {
          // Check if any errors exist on confirmation fields (Zod mismatch)
          // If they are filled, check if Zod reported match errors
          const currentErrors = form.formState.errors;
          if (
            ((!paymentPNR || isEmailModified) && currentErrors.confirmEmail) ||
            ((!paymentPNR || isPhoneModified) &&
              (currentErrors.confirmCountry || currentErrors.confirmPhone))
          ) {
            areConfirmFieldsValid = false;
          }
        }
      }

      let isAdditionalValid = true;
      if (showAdditionalEmail && additionalEmail && !isValidEmail(additionalEmail)) {
        isAdditionalValid = false;
      }

      if (!isFormValid || !areConfirmFieldsValid || !isAdditionalValid) {
        dispatch(setContactErrorMessage("All fields must be completed and match."));
        const el = document.getElementById("contact-info-section");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        // Trigger validation to show visual errors on UI (untouched fields)
        trigger();

        return false;
      } else {
        dispatch(setContactErrorMessage(null));
        return true;
      }
    };

    return () => {
      globalThis.__validateContactInfo = undefined;
    };
  }, [
    trigger,
    showConfirmFields,
    dispatch,
    getValues,
    form,
    showAdditionalEmail,
    additionalEmail,
    isValidEmail,
    isValid,
    isEmailModified,
    isPhoneModified,
    paymentPNR,
  ]);

  // Auto-show confirmation fields
  useEffect(() => {
    if (paymentPNR && Number(bookingSummary?.contacts?.length) > 0) {
      setShowConfirmFields(isContactModified);
      return;
    }

    if (
      email &&
      countryCode &&
      phoneNumber &&
      phoneNumber.length === 9 &&
      // Check if core fields are valid before showing confirm
      !form.formState.errors.email &&
      !showConfirmFields
    ) {
      if (!globalThis.location.pathname.includes("/modify-booking/payment")) {
        setShowConfirmFields(true);
      }

      // Pre-fill country to match if not set
      if (!getValues("confirmCountry")) {
        setValue("confirmCountry", countryCode, { shouldValidate: true });
      }
    }
  }, [
    email,
    countryCode,
    phoneNumber,
    showConfirmFields,
    form.formState.errors,
    getValues,
    setValue,
    paymentPNR,
    bookingSummary?.contacts?.length,
    isContactModified,
  ]);

  // Clear global error message when user interacts
  useEffect(() => {
    if (contactErrorMessage && (firstName || lastName || email || phoneNumber)) {
      // Logic to clear global error could go here if desired
    }
  }, [firstName, lastName, email, phoneNumber, contactErrorMessage]);

  // Also clear if confirmation fields match
  useEffect(() => {
    if (showConfirmFields && (confirmEmail || confirmPhone || confirmCountry)) {
      if (!errors.confirmEmail && !errors.confirmPhone && !errors.confirmCountry) {
        dispatch(setContactErrorMessage(null));
      }
    }
  }, [confirmEmail, confirmPhone, confirmCountry, showConfirmFields, dispatch, errors]);

  const requiredLabel = (label?: string) => {
    const text = label?.trim() ?? "";
    return text.endsWith("*") ? text : `${text}*`;
  };

  const fieldLabelClassName = "text-sm text-gray-700";

  const fieldClassName = "w-full h-12 px-4 text-base rounded-lg";

  const countrySelectClassName =
    "w-full h-12 pl-4 pr-10 text-base bg-white border-[1.5px] rounded-lg appearance-none transition-all duration-200 focus:outline-none focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#BFDEFF]";

  return (
    <div className="w-full" id="contact-info-section" data-testid="contact-info-section">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="text-brand-blue">
          <Image src={userIcon} alt="info icon" width={35} height={35} />
        </span>
        <span
          className="text-lg font-semibold text-brand-blue"
          data-testid="contact-info-subtitle-lbl"
        >
          {pageData?.heading[0]?.subtitle}
        </span>
      </div>
      {/* Notification Banner */}
      <div className="flex items-start gap-2 p-2 my-3 text-sm text-white rounded-md bg-brand-cyan">
        <Image src={infoIcon} width={20} height={20} alt="info icon" />
        <div
          data-testid="contact-info-description-lbl"
          dangerouslySetInnerHTML={{
            __html: pageData?.warningBlock?.[0]?.description || "",
          }}
        />
      </div>

      <Card className="py-2 border shadow-sm bg-muted md:py-5">
        <CardContent className="px-3 space-y-2 md:px-5">
          <Form {...form}>
            {/* Checkbox */}
            {!paymentPNR && (
              <label
                htmlFor="first-passenger"
                className="flex items-center gap-3 mb-4 cursor-pointer"
              >
                <div className="relative flex items-center justify-center">
                  <RoundedCheckbox
                    id="first-passenger"
                    size="md"
                    tickSize="md"
                    checked={isFirstPassengerChecked}
                    onCheckedChange={(checked) => {
                      setValue("isFirstPassenger", checked as boolean);
                      handleFormChange("isFirstPassenger", checked as boolean);
                      if (checked) {
                        // handled by effect
                      } else {
                        clearFirstPassengerData();
                      }
                    }}
                    data-testid="first-passenger-chk"
                  />
                </div>
                <span className="text-sm text-gray-700">{pageData?.contactCardTitle}</span>
              </label>
            )}

            {isFirstPassengerChecked && !firstPassenger && !bookingDetails && (
              <div className="p-2 mb-4 text-xs text-orange-600 bg-orange-100 rounded-md">
                <p>First passenger data not found. Please complete the information manually.</p>
              </div>
            )}

            {/* Name Fields */}
            <div className="grid grid-cols-1 gap-2 sm:gap-4 md:grid-cols-2">
              <div>
                <FormField
                  control={control}
                  name="firstName"
                  render={({ field }) => (
                    <InputField
                      {...field}
                      placeholder={pageData.firstNamePlaceholder}
                      className={cn(
                        "w-full",
                        errors.firstName &&
                          "border-red-500 focus:border-red-500 focus:ring-red-500",
                      )}
                      maxLength={32}
                      onChange={(e) => {
                        field.onChange(e); // update form state
                        handleFormChange("firstName", e.target.value); // update redux
                      }}
                      disabled={Boolean(isFirstPassengerChecked || isContactLocked)}
                    />
                  )}
                />
                {errors.firstName && (
                  <p className="text-xs text-red-500 mt-1">{errors.firstName.message}</p>
                )}
              </div>
              <div>
                <FormField
                  control={control}
                  name="lastName"
                  render={({ field }) => (
                    <InputField
                      {...field}
                      placeholder={pageData.lastNamePlaceholder}
                      className={cn(
                        "w-full",
                        errors.lastName && "border-red-500 focus:border-red-500 focus:ring-red-500",
                      )}
                      maxLength={32}
                      onChange={(e) => {
                        field.onChange(e);
                        handleFormChange("lastName", e.target.value);
                      }}
                      disabled={Boolean(isFirstPassengerChecked || isContactLocked)}
                    />
                  )}
                />
                {errors.lastName && (
                  <p className="text-xs text-red-500 mt-1">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            {/* RUC Field (Conditional) */}
            {/* {(() => {
              const normalized = (localeFromServer ?? "").trim().toLowerCase().replace("-", "_");
              const showRuc = normalized === "es_ec";
              return (
                showRuc && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <InputField
                      control={control}
                      name="RUC"
                      placeholder="RUC"
                      className="grid w-full grid-cols-2"
                      onChange={(e) => setValue("RUC", e.target.value)}
                      disabled={false}
                    />
                    <div className="hidden sm:block" />
                  </div>
                )
              );
            })()} */}

            {/* Email, Country, Phone Fields */}
            <div className="grid items-start grid-cols-1 gap-4 md:grid-cols-4">
              <div className="flex flex-col gap-2 md:col-span-2">
                <label htmlFor="email" className={fieldLabelClassName}>
                  {pageData.emailPlaceholder}
                </label>
                <FormField
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <InputField
                      {...field}
                      id="email"
                      type="email"
                      placeholder={pageData.emailPlaceholder}
                      className={cn(
                        fieldClassName,
                        errors.email && "border-red-500 focus:border-red-500 focus:ring-red-500",
                      )}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(e);
                        handleFormChange("email", val);
                        if (paymentPNR) {
                          if (val === initialEmailRef.current) {
                            setValue("confirmEmail", val, { shouldValidate: true });
                          } else if (getValues("confirmEmail") === initialEmailRef.current) {
                            setValue("confirmEmail", "", { shouldValidate: true });
                          } else {
                            trigger("confirmEmail");
                          }
                        } else {
                          trigger("confirmEmail");
                        }
                      }}
                      disabled={false}
                    />
                  )}
                />
                {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
              </div>

              {/* Custom Country Select */}
              <div className="flex flex-col w-full gap-2">
                <label htmlFor="country-code" className={fieldLabelClassName}>
                  {requiredLabel(pageData.countryCodeLabel)}
                </label>
                <div className="relative">
                  <select
                    id="country-code"
                    className={cn(
                      countrySelectClassName,
                      errors.country
                        ? "border-[#CF4D54] focus-visible:border-[#CF4D54] focus-visible:ring-[#CF4D54]"
                        : "border-[var(--brand-black-60)] focus-visible:border-[var(--brand-blue-20)]",
                      "disabled:border-brand-black-60 disabled:text-brand-black-60",
                    )}
                    value={countryCode}
                    onChange={(e) => {
                      const value = e.target.value;
                      setValue("country", value, { shouldValidate: true });
                      handleFormChange("country", value);
                      setMaxLengthReached(false);
                      if (paymentPNR) {
                        if (value === initialCountryRef.current && !isPhoneModified) {
                          setValue("confirmCountry", value, { shouldValidate: true });
                        } else {
                          trigger(["phoneNumber", "confirmCountry"]);
                        }
                      } else {
                        trigger(["phoneNumber", "confirmCountry"]);
                      }
                    }}
                    disabled={false}
                    data-testid="country-code-dropdown"
                  >
                    {COUNTRIES_CALLING_CODES.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        data-testid={`country-code-${option.value}-option`}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    aria-hidden
                    className="absolute w-5 h-5 -translate-y-1/2 pointer-events-none text-brand-black-60 right-3 top-1/2"
                  />
                </div>
                {errors.country && <p className="text-xs text-red-500">{errors.country.message}</p>}
              </div>

              <div className="flex flex-col w-full gap-2">
                <span aria-hidden className={cn("hidden md:block", fieldLabelClassName)}>
                  &nbsp;
                </span>
                <FormField
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <InputField
                      {...field}
                      id="phoneNumber"
                      type="tel"
                      placeholder={pageData.phoneNumberLabel}
                      aria-label={pageData.phoneNumberLabel}
                      className={cn(
                        fieldClassName,
                        errors.phoneNumber &&
                          "border-red-500 focus:border-red-500 focus:ring-red-500",
                      )}
                      inputMode="numeric"
                      maxLength={17}
                      onChange={(e) => {
                        const digitsFormat = toDigits(e.target.value);
                        const digits =
                          countryCode === phoneCountryCode.Chile
                            ? digitsFormat.slice(0, 9)
                            : digitsFormat.slice(0, 16);
                        field.onChange(digits);
                        handleFormChange("phoneNumber", digits);
                        if (paymentPNR) {
                          if (digits === initialPhoneRef.current) {
                            setValue("confirmPhone", digits, { shouldValidate: true });
                          } else if (getValues("confirmPhone") === initialPhoneRef.current) {
                            setValue("confirmPhone", "", { shouldValidate: true });
                          } else {
                            trigger("confirmPhone");
                          }
                        } else {
                          trigger("confirmPhone");
                        }
                        if (digitsFormat.length > 16) {
                          setMaxLengthReached(true);
                        } else {
                          setMaxLengthReached(false);
                        }
                      }}
                      disabled={false}
                    />
                  )}
                />
                {errors.phoneNumber && (
                  <p className="text-xs text-red-500">{errors.phoneNumber.message}</p>
                )}
                {maxLengthReached && countryCode !== phoneCountryCode.Chile && (
                  <p className="text-xs text-red-500">The maximum length is 16 characters.</p>
                )}
              </div>
            </div>

            {/* Confirmation Section */}
            {showConfirmFields && (
              <div className="mt-4 space-y-4">
                <div className="flex items-start gap-2 p-2 my-3 text-sm text-white rounded-md bg-brand-cyan">
                  <p>{pageData?.reEnterDetailsMessage}</p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label htmlFor="confirmEmail" className={fieldLabelClassName}>
                      {pageData.confirmEmailAddressPlaceholder || "Confirm email address"}
                    </label>
                    <FormField
                      control={control}
                      name="confirmEmail"
                      render={({ field }) => (
                        <InputField
                          {...field}
                          id="confirmEmail"
                          type="email"
                          className={fieldClassName}
                          disabled={Boolean(paymentPNR && !isEmailModified)}
                          onPaste={(e) => e.preventDefault()}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => e.preventDefault()}
                        />
                      )}
                    />
                    {errors.confirmEmail && (
                      <p className="text-sm text-red-600">{errors.confirmEmail.message}</p>
                    )}
                  </div>

                  {/* Country Code Confirmation */}
                  <div className="flex flex-col w-full gap-2">
                    <label htmlFor="confirm-country-code" className={fieldLabelClassName}>
                      {requiredLabel(pageData.countryCodeLabel)}
                    </label>
                    <div className="relative">
                      <select
                        id="confirm-country-code"
                        className={cn(
                          countrySelectClassName,
                          errors.confirmCountry
                            ? "border-[#CF4D54] focus-visible:border-[#CF4D54] focus-visible:ring-[#CF4D54]"
                            : "border-[var(--brand-black-60)] focus-visible:border-[var(--brand-blue-20)]",
                          "disabled:border-brand-black-60 disabled:text-brand-black-60",
                        )}
                        value={confirmCountry ?? ""}
                        disabled={Boolean(paymentPNR && !isPhoneModified)}
                        onChange={(e) => {
                          setValue("confirmCountry", e.target.value, { shouldValidate: true });
                        }}
                        data-testid="country-code-dropdown"
                      >
                        <option value="" />
                        {COUNTRIES_CALLING_CODES.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                            data-testid={`country-code-${option.value}-option`}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        aria-hidden
                        className="absolute w-5 h-5 -translate-y-1/2 pointer-events-none text-brand-black-60 right-3 top-1/2"
                      />
                    </div>
                    {errors.confirmCountry && (
                      <p className="text-sm text-red-600">{errors.confirmCountry.message}</p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <span aria-hidden className={cn("hidden md:block", fieldLabelClassName)}>
                      &nbsp;
                    </span>
                    <FormField
                      control={control}
                      name="confirmPhone"
                      render={({ field }) => (
                        <InputField
                          {...field}
                          id="confirmPhone"
                          type="tel"
                          placeholder={pageData.confirmPhonePlaceholder || "Confirm phone*"}
                          className={fieldClassName}
                          disabled={Boolean(paymentPNR && !isPhoneModified)}
                          inputMode="numeric"
                          maxLength={17}
                          onChange={(e) => {
                            const digitsFormat = toDigits(e.target.value);
                            const digits = digitsFormat.slice(0, digitsFormat.length);
                            field.onChange(digits);
                          }}
                          onPaste={(e) => e.preventDefault()}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => e.preventDefault()}
                        />
                      )}
                    />
                    {errors.confirmPhone && (
                      <p className="text-sm text-red-600">{errors.confirmPhone.message}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Add Email Link */}
            <div className="pt-2">
              <button
                type="button"
                onClick={addAdditionalEmail}
                className="text-sm font-medium transition-colors text-brand-cyan hover:text-brand-blue"
                disabled={showAdditionalEmail}
                data-testid="add-additional-email-btn"
              >
                {pageData.emailLabel}
              </button>
            </div>

            {/* Additional Email Field */}
            {showAdditionalEmail && (
              <div className="mt-2">
                <div className="relative">
                  <input
                    type="email"
                    placeholder="Email (2)"
                    className={`w-full h-[42px] px-3 pr-20 text-base border rounded-md focus:outline-none bg-white ${additionalEmailError ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"}`}
                    value={additionalEmail}
                    onChange={(e) => {
                      setAdditionalEmail(e.target.value);
                      handleFormChange("additionalEmail", e.target.value);
                    }}
                    data-testid="add-additional-email-input"
                  />
                  <button
                    type="button"
                    onClick={removeAdditionalEmail}
                    className="absolute text-sm font-medium text-red-600 transition-colors transform -translate-y-1/2 right-3 top-1/2 hover:text-red-800"
                    data-testid="delete-additional-email-btn"
                  >
                    Delete X
                  </button>
                </div>
                {additionalEmailError && (
                  <p className="mt-1 text-sm text-red-600">{additionalEmailError}</p>
                )}
              </div>
            )}

            {/* Error message should appear below the section, after the "+ Add email address" link */}
            {contactErrorMessage && (
              <div className="mt-3">
                <div className="bg-red-100 text-red-700 text-sm font-medium rounded-md px-3 py-2 text-center">
                  {contactErrorMessage}
                </div>
              </div>
            )}
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
